import { useEffect } from 'react';

import type { Lang } from '@/types';

/** URL d'une meme page dans chaque langue, pour les `hreflang` reciproques. */
export interface SeoAlternates {
  fr: string;
  en: string;
}

export interface SeoConfig {
  title: string;
  description: string;
  canonical: string;
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  jsonLd?: readonly object[];
  /** Langue de la page : pose `<html lang>` et `og:locale`. */
  lang?: Lang;
  /** Absent = page monolingue (les articles, en francais seulement). */
  alternates?: SeoAlternates;
}

const OG_LOCALE: Record<Lang, string> = { fr: 'fr_CA', en: 'en_CA' };

/** Marque les noeuds poses par le hook, pour les retirer au demontage. */
const MARKER = 'data-seo';
const DEFAULT_OG_IMAGE = 'https://pierresere.com/og-image.png';

/** Cree un element marque et l'ajoute au head. */
function appendTagged(tag: 'meta' | 'link' | 'script'): Element {
  const element = document.createElement(tag);
  element.setAttribute(MARKER, 'dynamic');
  document.head.appendChild(element);
  return element;
}

/** Pose une balise meta[property] (Open Graph). */
function setProperty(property: string, content: string): void {
  const element = appendTagged('meta');
  element.setAttribute('property', property);
  element.setAttribute('content', content);
}

/** Pose une balise meta[name] (description, Twitter). */
function setName(name: string, content: string): void {
  const element = appendTagged('meta');
  element.setAttribute('name', name);
  element.setAttribute('content', content);
}

/** Pose le rel=canonical. */
function setCanonical(href: string): void {
  const element = appendTagged('link');
  element.setAttribute('rel', 'canonical');
  element.setAttribute('href', href);
}

/** Pose un rel=alternate hreflang. */
function setAlternate(hreflang: string, href: string): void {
  const element = appendTagged('link');
  element.setAttribute('rel', 'alternate');
  element.setAttribute('hreflang', hreflang);
  element.setAttribute('href', href);
}

/** Injecte un bloc JSON-LD. */
function setJsonLd(data: object): void {
  const element = appendTagged('script');
  element.setAttribute('type', 'application/ld+json');
  element.textContent = JSON.stringify(data);
}

/** Retire tous les noeuds poses par un rendu precedent. */
function clearTagged(): void {
  for (const node of document.head.querySelectorAll(`[${MARKER}="dynamic"]`)) {
    node.remove();
  }
}

/**
 * Pose title, description, canonical, Open Graph, Twitter Card et JSON-LD
 * pour la route courante. react-snap capture le DOM apres execution du JS,
 * donc ces balises se retrouvent bien dans le HTML statique servi a Google.
 */
export function useSeo(config: SeoConfig): void {
  const { title, description, canonical, ogType, ogImage, jsonLd, lang, alternates } = config;

  useEffect(() => {
    const image = ogImage ?? DEFAULT_OG_IMAGE;
    const type = ogType ?? 'website';
    const pageLang = lang ?? 'fr';

    clearTagged();
    document.title = title;

    // react-snap capture le DOM apres execution du JS : l'attribut pose ici se
    // retrouve dans le HTML statique, et chaque page est servie a Google avec
    // la langue qui est reellement la sienne.
    document.documentElement.lang = pageLang;

    setName('description', description);
    setCanonical(canonical);

    // Reciprocite obligatoire : chaque version doit declarer l'autre ET
    // elle-meme, sinon Google ignore l'alternance. x-default = francais.
    if (alternates !== undefined) {
      setAlternate('fr-CA', alternates.fr);
      setAlternate('en', alternates.en);
      setAlternate('x-default', alternates.fr);
    }

    setProperty('og:type', type);
    setProperty('og:url', canonical);
    setProperty('og:title', title);
    setProperty('og:description', description);
    setProperty('og:image', image);
    setProperty('og:locale', OG_LOCALE[pageLang]);
    setProperty('og:site_name', 'Pierre Séré');

    setName('twitter:card', 'summary_large_image');
    setName('twitter:title', title);
    setName('twitter:description', description);
    setName('twitter:image', image);

    for (const block of jsonLd ?? []) setJsonLd(block);

    return clearTagged;
  }, [title, description, canonical, ogType, ogImage, jsonLd, lang, alternates]);

  // Remonter en haut UNIQUEMENT au changement de page : l'effet principal se
  // rejoue aussi quand un rendu recree jsonLd/alternates (ex. bascule de
  // theme), et scroller a ce moment-la renverrait le visiteur en haut du site.
  useEffect(() => {
    // 'instant' : le scroll-behavior:smooth global ferait animer la remontee,
    // et l'animation peut etre interrompue par le rendu de la nouvelle page.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [canonical]);
}
